<template>
  <div class="supplier-list-wrapper">
    <!-- Toast Notifications -->
    <div class="toast-container">
      <CToaster :autohide="3000">
        <CToast
          v-for="(info, index) in infoList"
          :key="`toast-${index}-${Date.now()}`"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}
        </CToast>
      </CToaster>
    </div>

    <!-- Main Content -->
    <CRow>
      <CCol>
        <CCard class="main-card">
          <CCardHeader class="professional-header">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="header-title">
                <h5 class="mb-0">
                  <i class="fas fa-handshake me-2"></i>
                  <strong>Supplier Management</strong>
                </h5>
                <small class="text-muted">Manage vehicle suppliers and partners</small>
              </div>
              <div class="header-actions">
                <CBadge color="info" class="result-counter">
                  {{ items.length }} {{ items.length === 1 ? 'Supplier' : 'Suppliers' }}
                </CBadge>
              </div>
            </div>
          </CCardHeader>

          <CCardBody>
            <!-- Loading State -->
            <div v-if="loading && items.length === 0" class="loading-state">
              <div class="text-center py-5">
                <CSpinner color="primary" />
                <p class="text-muted mt-3">Loading suppliers...</p>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="!loading && items.length === 0" class="empty-state">
              <div class="text-center py-5">
                <i class="fas fa-handshake fa-3x text-muted mb-3"></i>
                <h5 class="text-muted">No suppliers found</h5>
                <p class="text-muted">Get started by adding your first supplier</p>
                <CButton
                  v-if="canAdd"
                  color="primary"
                  @click="addNew"
                  class="mt-2"
                >
                  <i class="fas fa-plus me-2"></i>
                  Add New Supplier
                </CButton>
              </div>
            </div>

            <!-- Data View (Mobile + Desktop) -->
            <div v-else>
            <!-- Mobile Card View -->
            <div class="mobile-view d-md-none">
              <div v-for="(item, index) in items" :key="item.id" class="mobile-card">
                <div class="mobile-card-header" @click="onEdit(item)">
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="flex-grow-1">
                      <div class="supplier-number text-muted mb-1">
                        <small>#{{ index + 1 }}</small>
                      </div>
                      <h6 class="supplier-name mb-1">
                        <i class="fas fa-handshake me-2 text-primary"></i>
                        {{ item.name }}
                        <span v-if="item.code" class="text-muted ms-2">
                          <small>({{ item.code }})</small>
                        </span>
                      </h6>
                      <div class="supplier-details">
                        <div v-if="item.country" class="text-muted mb-1">
                          <small>
                            <i class="fas fa-globe me-1"></i>
                            {{ item.country }}
                          </small>
                        </div>
                        <div v-if="item.phone" class="text-muted mb-1">
                          <small>
                            <i class="fas fa-phone me-1"></i>
                            {{ item.phone }}
                          </small>
                        </div>
                        <div v-if="item.contactPersonName" class="text-muted mb-1">
                          <small>
                            <i class="fas fa-user me-1"></i>
                            Contact: {{ item.contactPersonName }}
                          </small>
                        </div>
                        <div v-if="item.contactPersonPhone" class="text-muted mb-1">
                          <small>
                            <i class="fas fa-mobile-alt me-1"></i>
                            {{ item.contactPersonPhone }}
                          </small>
                        </div>
                        <div v-if="item.contactPersonEmail" class="text-muted">
                          <small>
                            <i class="fas fa-envelope me-1"></i>
                            {{ item.contactPersonEmail }}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mobile-card-footer">
                  <div class="d-flex gap-2">
                    <CButton
                      size="sm"
                      :color="canUpdate ? 'primary' : 'info'"
                      variant="outline"
                      @click="onEdit(item)"
                      class="flex-grow-1"
                    >
                      <i :class="canUpdate ? 'fas fa-edit me-1' : 'fas fa-eye me-1'"></i>
                      {{ canUpdate ? 'Edit' : 'View' }}
                    </CButton>
                    <CButton
                      v-if="canDelete"
                      size="sm"
                      color="danger"
                      variant="outline"
                      @click="showDeleteConfirmation(item)"
                    >
                      <i class="fas fa-trash me-1"></i>
                      Delete
                    </CButton>
                  </div>
                </div>
              </div>
            </div>

            <!-- Desktop Table View -->
            <div class="desktop-view d-none d-md-block">
              <div class="table-responsive">
                <CDataTable
                  :items="items"
                  :fields="fields"
                  :hover="true"
                  :loading="loading"
                  column-filter
                  :items-per-page="20"
                  sorter
                  pagination
                  @row-clicked="onEdit"
                  class="professional-table"
                >
                  <template #show_index="{ index }">
                    <td class="text-center">
                      <span class="text-muted">#{{ index + 1 }}</span>
                    </td>
                  </template>

                  <template #name="{ item }">
                    <td>
                      <div class="d-flex align-items-center">
                        <i class="fas fa-handshake text-primary me-2"></i>
                        <strong>{{ item.name }}</strong>
                      </div>
                    </td>
                  </template>

                  <template #code="{ item }">
                    <td>
                      <span v-if="item.code" class="badge bg-secondary">
                        {{ item.code }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </template>

                  <template #country="{ item }">
                    <td>
                      <span v-if="item.country" class="text-muted">
                        <i class="fas fa-globe me-1"></i>
                        {{ item.country }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </template>

                  <template #phone="{ item }">
                    <td>
                      <span v-if="item.phone" class="text-muted">
                        <i class="fas fa-phone me-1"></i>
                        {{ item.phone }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </template>

                  <template #contactPersonName="{ item }">
                    <td>
                      <span v-if="item.contactPersonName" class="text-muted">
                        <i class="fas fa-user me-1"></i>
                        {{ item.contactPersonName }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </template>

                  <template #contactPersonPhone="{ item }">
                    <td>
                      <span v-if="item.contactPersonPhone" class="text-muted">
                        <i class="fas fa-mobile-alt me-1"></i>
                        {{ item.contactPersonPhone }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </template>

                  <template #contactPersonEmail="{ item }">
                    <td>
                      <span v-if="item.contactPersonEmail" class="text-muted">
                        <i class="fas fa-envelope me-1"></i>
                        {{ item.contactPersonEmail }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </template>

                  <template #show_details="{ item }">
                    <td class="text-end">
                      <CButton
                        :color="canUpdate ? 'primary' : 'info'"
                        variant="outline"
                        size="sm"
                        @click.stop="onEdit(item)"
                        class="me-1"
                      >
                        <i :class="canUpdate ? 'fas fa-edit me-1' : 'fas fa-eye me-1'"></i>
                        {{ canUpdate ? 'Edit' : 'View' }}
                      </CButton>
                      <CButton
                        v-if="canDelete"
                        color="danger"
                        variant="outline"
                        size="sm"
                        @click.stop="showDeleteConfirmation(item)"
                      >
                        <i class="fas fa-trash me-1"></i>
                        Delete
                      </CButton>
                    </td>
                  </template>
                </CDataTable>
              </div>
            </div>
            </div>
          </CCardBody>

          <!-- Footer -->
          <CCardFooter class="card-footer">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="footer-actions">
                <CButton v-if="canAdd" color="primary" @click="addNew">
                  <i class="fas fa-plus me-2"></i>
                  Add New Supplier
                </CButton>
                <CButton
                  color="success"
                  variant="outline"
                  @click="exportCSV"
                  class="ms-2"
                >
                  <i class="fas fa-download me-2"></i>
                  Export CSV
                </CButton>
              </div>
              <div class="footer-info">
                <small class="text-muted">
                  Total: {{ items.length }} {{ items.length === 1 ? 'supplier' : 'suppliers' }}
                </small>
              </div>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>

    <!-- Delete Confirmation Modal -->
    <CModal :show.sync="warningModal" color="warning">
      <template #header>
        <h5 class="modal-title">
          <i class="fas fa-exclamation-triangle me-2"></i>
          Confirm Delete
        </h5>
      </template>
      <p>Are you sure you want to delete <strong>{{ itemToDelete.name }}</strong>?</p>
      <p class="text-muted mb-0">
        <small>This action cannot be undone.</small>
      </p>
      <template #footer>
        <CButton color="secondary" @click="warningModal = false">
          Cancel
        </CButton>
        <CButton color="danger" @click="onDeleteConfirmation">
          <i class="fas fa-trash me-2"></i>
          Delete
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import SupplierApi from "../../lib/supplierApi";

export default {
  name: "SupplierList",
  data() {
    return {
      loading: true,
      items: [],
      infoList: [],
      fields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:60px",
          sorter: false,
          filter: false,
        },
        {
          key: "name",
          label: "Supplier Name",
          _style: "min-width:200px;"
        },
        {
          key: "code",
          label: "Code",
          _style: "width:100px;"
        },
        {
          key: "country",
          label: "Country",
          _style: "min-width:120px;"
        },
        {
          key: "phone",
          label: "Phone",
          _style: "min-width:130px;"
        },
        {
          key: "contactPersonName",
          label: "Contact Person",
          _style: "min-width:150px;"
        },
        {
          key: "contactPersonPhone",
          label: "Contact Phone",
          _style: "min-width:130px;"
        },
        {
          key: "contactPersonEmail",
          label: "Contact Email",
          _style: "min-width:200px;"
        },
        {
          key: "show_details",
          label: "",
          _style: "width:180px",
          sorter: false,
          filter: false,
        },
      ],
      api: new SupplierApi(),
      warningModal: false,
      itemToDelete: {},
    };
  },
  computed: {
    canView() {
      return this.$auth.hasSubModulePermission('SUPPLIERS', 'CanView');
    },
    canAdd() {
      return this.$auth.hasSubModulePermission('SUPPLIERS', 'CanAdd');
    },
    canDelete() {
      return this.$auth.hasSubModulePermission('SUPPLIERS', 'CanDelete');
    },
    canUpdate() {
      return this.$auth.hasSubModulePermission('SUPPLIERS', 'CanUpdate');
    }
  },
  mounted() {
    // Check view permission
    if (!this.canView) {
      this.$router.push({ name: 'Page403' });
      return;
    }
    this.refreshTable();
  },
  methods: {
    toast(header, message, color) {
      this.infoList.push({
        header: header,
        message: message,
        color: color,
      });
    },

    refreshTable() {
      this.loading = true;
      this.api
        .getList()
        .then((response) => {
          this.items = response.result || [];
          this.loading = false;
        })
        .catch((error) => {
          this.toast("Error", "Failed to load suppliers", "danger");
          this.loading = false;
        });
    },

    onEdit(item) {
      this.$router.push({
        path: `/admins/Supplier/${item.id}`,
      });
    },

    addNew() {
      this.$router.push({ path: "/admins/Supplier" });
    },

    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },

    onDeleteConfirmation() {
      this.api
        .delete(this.itemToDelete.id)
        .then((response) => {
          this.refreshTable();
          this.toast("Success", "Supplier deleted successfully", "success");
          this.warningModal = false;
          this.itemToDelete = {};
        })
        .catch((error) => {
          this.toast("Error", "Failed to delete supplier", "danger");
          this.warningModal = false;
        });
    },

    exportCSV() {
      const csvData = this.items.map((item, index) => ({
        '#': index + 1,
        'Supplier Name': item.name || '',
        'Code': item.code || '',
        'Country': item.country || '',
        'Phone': item.phone || '',
        'Contact Person': item.contactPersonName || '',
        'Contact Phone': item.contactPersonPhone || '',
        'Contact Email': item.contactPersonEmail || '',
      }));

      const headers = Object.keys(csvData[0]);
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => headers.map(header => `"${row[header]}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `suppliers_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.toast("Success", "CSV exported successfully", "success");
    },
  },
};
</script>

<style scoped>
/* Professional Container */
.supplier-list-wrapper {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Main Card */
.main-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Professional Header */
.professional-header {
  background: white;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1.25rem 1.5rem;
}

.header-title h5 {
  font-size: 1.25rem;
  color: var(--cui-body-color, #212529);
  margin-bottom: 0.25rem;
}

.header-title small {
  font-size: 0.875rem;
}

.result-counter {
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.5rem 0.75rem;
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state i {
  font-size: 3rem;
  opacity: 0.5;
}

/* Mobile Card View */
.mobile-view {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.mobile-card {
  background: white;
  border: 1px solid var(--cui-border-color, #d8dbe0);
  border-radius: 0.375rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  overflow: hidden;
  transition: box-shadow 0.2s ease;
}

.mobile-card:hover {
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.1);
}

.mobile-card-header {
  padding: 1rem 1.25rem;
  cursor: pointer;
  background: white;
}

.mobile-card-header:hover {
  background: var(--cui-body-bg, #f8f9fa);
}

.supplier-number {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.supplier-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--cui-body-color, #212529);
}

.supplier-details {
  margin-top: 0.5rem;
  font-size: 0.875rem;
}

.mobile-card-footer {
  padding: 0.75rem 1.25rem;
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Desktop Table View */
.desktop-view {
  /* Uses CoreUI's default table styling */
}

.professional-table {
  font-size: 0.875rem;
}

.professional-table th {
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.professional-table tbody tr {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.professional-table tbody tr:hover {
  background-color: var(--cui-table-hover-bg, rgba(0, 0, 0, 0.075));
}

/* Footer */
.card-footer {
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.5rem;
}

.footer-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .supplier-list-wrapper {
    padding: 1rem;
  }

  .professional-header {
    padding: 1rem;
  }

  .header-title h5 {
    font-size: 1.125rem;
  }

  .card-footer {
    padding: 1rem;
  }

  .footer-actions {
    width: 100%;
  }

  .footer-actions .btn {
    flex: 1;
  }
}

/* Print Styles */
@media print {
  .toast-container,
  .card-footer,
  .mobile-card-footer,
  .show_details {
    display: none !important;
  }

  .mobile-view {
    display: none !important;
  }

  .desktop-view {
    display: block !important;
  }
}
</style>
